/**
 * DeployConfirmModal
 *
 * Confirmation dialog for deploying a draft version to live.
 * Requires a deployment note before proceeding.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Textarea } from '../../../ui/textarea';
import { Label } from '../../../ui/label';

interface DeployConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versionNumber: string;
  type: 'taxonomy' | 'prompt';
  onConfirm: (deploymentNote: string) => void;
  isDeploying?: boolean;
  signalCount?: number;
}

const DeployConfirmModal: React.FC<DeployConfirmModalProps> = ({
  open,
  onOpenChange,
  versionNumber,
  type,
  onConfirm,
  isDeploying = false,
  signalCount,
}) => {
  const [note, setNote] = useState('');

  const handleConfirm = () => {
    if (note.trim()) {
      onConfirm(note.trim());
      setNote('');
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setNote('');
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Deploy v{versionNumber} to Live</DialogTitle>
          <DialogDescription>
            This will replace the current live {type} version. The existing live
            version will be archived automatically. This action takes effect
            immediately.
          </DialogDescription>
          {signalCount != null && signalCount > 0 && (
            <p className="text-sm text-amber-600 mt-2">
              {signalCount.toLocaleString()} classified signals will need reclassification after deployment.
            </p>
          )}
        </DialogHeader>

        <div className="py-4">
          <Label htmlFor="deployment-note" className="text-sm font-medium">
            Deployment Note <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="deployment-note"
            placeholder={`Describe what changed in this ${type} version...`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-2"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">
            This note will be recorded in the audit trail.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isDeploying}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!note.trim() || isDeploying}
          >
            {isDeploying ? 'Deploying...' : 'Deploy to Live'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeployConfirmModal;
